import React, { FC, useMemo } from 'react'
import { Space, Tooltip } from 'antd'
import {
  CloudOutlined,
  KeyOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { OrganizationWithPermissions } from 'services'
import { OrganizationRoleEnum } from 'services/types'
import { CardButton } from './CardButton'
import useAccountSubscriptions from 'hooks/useAccountSubscriptions'
import { PlanType } from '@pollination-solutions/pollination-sdk'
import { useAuth } from 'auth-context'

interface BodyProps {
  info: OrganizationWithPermissions
}

export const CardBody: FC<BodyProps> = ({ info }) => {
  const { subscriptions } = useAccountSubscriptions(info.account_name)
  const { user } = useAuth()
  
  const hasCloudSubs = useMemo(() => {
    if (!subscriptions) return false
    return subscriptions.filter((sub) => {
      return sub.plan_slug === 'professional-tier' 
      || PlanType.CloudCompute === sub.type
    }).length > 0
  }, [subscriptions])

  return (
    <Space      
      wrap
      size={'middle'}
      style={{
        width: '100%',
      }}
    >
      {info &&
        info.roles.some((role) =>
          [OrganizationRoleEnum.BillingAdmin, OrganizationRoleEnum.SuperAdmin].includes(role.roleName)
        ) && (
          <Tooltip title="Subscriptions Page">
            <CardButton 
              link={`/${info.account_name}?tab=subscription`}
              label={'Subscriptions'} 
              icon={<ThunderboltOutlined />}
            />
          </Tooltip>
        )}
      {info &&
        info.roles.some((role) =>
          [OrganizationRoleEnum.Member, OrganizationRoleEnum.LicenseAdmin, OrganizationRoleEnum.SuperAdmin].includes(role.roleName)
        ) && (
          <Tooltip title="License Pools Page">
            <CardButton 
              link={`/${info.roles.some((role) => role.roleName === OrganizationRoleEnum.Member) 
                ? user?.username : info.account_name}/licenses`}
              label={'License pools'} 
              icon={<KeyOutlined />}
              avatarStyle={{
                backgroundColor: '#5cb85c'
              }}
            />
          </Tooltip>
        )}
      {info && info.roles.some((role) => [OrganizationRoleEnum.Member, OrganizationRoleEnum.SuperAdmin].includes(role.roleName)) && (
        <Tooltip title="Members Page">
          <CardButton 
            link={`/${info.account_name}?tab=members`}
            label={'Members'} 
            avatarStyle={{
              backgroundColor: '#f0ad4e'
            }}
            icon={<UserOutlined />}
          />
        </Tooltip>
      )}
      {info && info.roles.some((role) => [OrganizationRoleEnum.SuperAdmin].includes(role.roleName)) && (
        <Tooltip title="Teams Page">
          <CardButton 
            link={`/${info.account_name}?tab=teams`}
            label={'Teams'} 
            icon={<TeamOutlined />}
            avatarStyle={{
              backgroundColor: '#d9534f'
            }}
          />
        </Tooltip>
      )}
      {info && info.roles.some((role) => [OrganizationRoleEnum.LicenseAdmin, 
        OrganizationRoleEnum.BillingAdmin].includes(role.roleName)) 
        ? null 
        : (hasCloudSubs && <Tooltip title="Simulation">
            <CardButton 
              link={`/${info.account_name}?tab=projects`}
              label={'Simulation'}
              icon={<CloudOutlined />}
              avatarStyle={{
                backgroundColor: '#999'
              }}
            />
          </Tooltip>)}
    </Space>
  )
}
